import { Dispatch, ReactElement, SetStateAction, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { IconTrash } from "@/components/_icons/IconTrash";
import { DeleteConfirmation } from "@/components/_molecules/delete-confirmation";
import { ErrorView } from "@/components/_molecules/error-view";
import { Loading } from "@/components/_molecules/loading-view";
import { DetailPageHeaderNav } from "@/components/_organisms/detail-view/detail-page-header-nav";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCanModuleAction } from "@/domain/_shared/module-access/hooks/use-can-module-action";
import { useEntityPermissions } from "@/domain/_shared/permission/hooks/use-entity-permissions";
import { useMunicipalPropertyYear } from "@/domain/municipal-property/hooks/api/use-municipal-property-year";
import { useMunicipalPropertyYearMutations } from "@/domain/municipal-property/hooks/api/use-municipal-property-year-mutations";
import { useRemoveMunicipalPropertyContent } from "@/domain/municipal-property/hooks/api/use-remove-municipal-property-content";
import { MUNICIPAL_PROPERTY_BASE_PATH } from "@/domain/municipal-property/routes/municipal-property-base-path";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { makeWorkspaceUrl } from "@/domain/workspace/lib/make-workspace-url";
import { isNonRetryableClientError } from "@/services/react-query.service/is-non-retryable-client-error";

export function MunicipalPropertyYearPage(): ReactElement {
  const { registerId = "", propertyYearId = "" }: { registerId?: string; propertyYearId?: string } = useParams<{ registerId: string; propertyYearId: string }>();
  const { t }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");
  const navigate: ReturnType<typeof useNavigate> = useNavigate();
  const { currentWorkspace }: ReturnType<typeof useCurrentWorkspaceContext> = useCurrentWorkspaceContext();
  const [isDeleteOpen, setIsDeleteOpen]: [boolean, Dispatch<SetStateAction<boolean>>] = useState(false);
  const { canWrite, canDelete: canDeleteRegister }: ReturnType<typeof useEntityPermissions> = useEntityPermissions({ entityId: registerId, resource: "municipalPropertyRegister" });
  const canCreate: boolean = useCanModuleAction({ module: "MUNICIPAL_PROPERTY", action: "create" });
  const { removeYear }: ReturnType<typeof useRemoveMunicipalPropertyContent> = useRemoveMunicipalPropertyContent({ canRestore: canDeleteRegister && canCreate });
  const handleRemove: () => Promise<void> = async (): Promise<void> => {
    try {
      await removeYear.mutateAsync(propertyYearId);
      setIsDeleteOpen(false);
      void navigate(makeWorkspaceUrl({ workspace: currentWorkspace, path: `${MUNICIPAL_PROPERTY_BASE_PATH}/registers/${registerId}` }));
    } catch {
      toast.error(t("register.property.removeError"));
    }
  };
  const { data: year, error, isLoading }: ReturnType<typeof useMunicipalPropertyYear> = useMunicipalPropertyYear(propertyYearId);

  const canUpdate: boolean = useCanModuleAction({ module: "MUNICIPAL_PROPERTY", action: "update" });
  const canDelete: boolean = useCanModuleAction({ module: "MUNICIPAL_PROPERTY", action: "delete" });

  const mutation: ReturnType<typeof useMunicipalPropertyYearMutations> = useMunicipalPropertyYearMutations();

  if (isLoading) return <Loading />;
  if (error && !isNonRetryableClientError(error)) return <ErrorView />;
  if (error || !year || year.registerId !== registerId) {
    return <p className="p-6 text-sm text-muted-foreground">{t("register.detail.notFound")}</p>;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DetailPageHeaderNav
        titleForm={
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold">
              {typeof year.effectiveYear !== "number" ? t("register.property.missingStand") : year.name}
            </h2>
            {year.municipalityName ? <p className="text-sm text-muted-foreground">{year.municipalityName}</p> : null}
            {typeof year.effectiveYear !== "number" ? (
              <p role="status" className="text-sm text-muted-foreground">
                {t("register.property.missingStandHelp", { year: year.year })}
              </p>
            ) : null}
          </div>
        }
      />
      <DeleteConfirmation
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title={t("register.property.removeConfirmTitle")}
        description={t("register.property.removeConfirmDescription")}
        onConfirm={() => void handleRemove()}
        onCancel={() => setIsDeleteOpen(false)}
      />
      <div className="min-h-0 flex-1">
        <Outlet
          context={{
            year,
            removeYearAction: canWrite && canDelete ? (
              <Button
                type="button"
                variant="destructive"
                size="xs"
                className="gap-2"
                disabled={removeYear.isPending}
                onClick={() => setIsDeleteOpen(true)}
              >
                <IconTrash className="size-3.5 shrink-0" />
                {t("register.property.remove")}
              </Button>
            ) : null,
            canWrite: canWrite && canUpdate && typeof year.effectiveYear === "number",
            mutation,
          }}
        />
      </div>
    </div>
  );
}
